import { Link } from "react-router-dom";
import moment from "moment";

import { cn } from "../../../../utils/classnames";
import { getBookingDetailPath } from "../constants";

/**
 * The month grid: six week rows of day cells, each holding the bookings that
 * start that day.
 *
 * Built by hand rather than with a calendar library so the day cells can carry
 * the assignment state — the whole point of this screen — and so the page keeps
 * the admin theme instead of importing a second one.
 */

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const VISIBLE_PER_DAY = 3;

// The bar down the left of a chip carries the booking status, the same three
// states the booking detail page shows. Missing assignments are a second,
// independent axis, so they get a dashed outline rather than another colour.
export const STATUS_BAR_COLORS = {
  CONFIRMED: "#3CAEA3",
  PENDING: "#F6BB2A",
  CANCELLED: "#EA4335",
};

// A chip is one click to the booking itself, opened in a new tab so the month
// stays where the admin left it. The day cell behind it still opens the day
// panel, which is where the vehicle and driver assignments are read.
const DayChip = ({ booking }) => (
  <Link
    to={getBookingDetailPath(booking)}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(event) => event.stopPropagation()}
    title={`${moment(booking.pickupDateTime).format("H:mm")} · ${booking.customerName}${
      booking.hasUnassigned ? " · needs vehicle & driver" : ""
    } — opens in a new tab`}
    className={cn(
      "tw:flex tw:w-full tw:items-center tw:gap-[6px] tw:rounded-[3px] tw:bg-[#F7F7F7] tw:px-[6px] tw:py-[3px] tw:text-left tw:!no-underline tw:hover:bg-[#EFEFEF]",
      booking.hasUnassigned
        ? "tw:border tw:border-dashed tw:border-[#C99A12]"
        : "tw:border-0",
    )}
  >
    <span
      className="tw:block tw:h-[14px] tw:w-[3px] tw:shrink-0 tw:rounded-full"
      style={{ backgroundColor: STATUS_BAR_COLORS[booking.effectiveStatus] || "#8D8D8D" }}
    />
    <span
      className={cn(
        "tw:truncate tw:text-[12px] tw:leading-[18px]",
        booking.effectiveStatus === "CANCELLED"
          ? "tw:!text-[#8D8D8D] tw:line-through"
          : "tw:!text-[#282828]",
      )}
    >
      <span className="tw:font-semibold">{moment(booking.pickupDateTime).format("H:mm")}</span>{" "}
      {booking.customerName}
    </span>
  </Link>
);

const CalendarMonthGrid = ({ month, bookingsByDay, selectedDay, onSelectDay }) => {
  // Explicitly Sunday-first rather than `startOf("week")`, which follows
  // whatever locale moment is on globally and would slide the header labels.
  const monthStart = month.clone().startOf("month");
  const monthEnd = month.clone().endOf("month");
  const gridStart = monthStart.clone().subtract(monthStart.day(), "days");
  const gridEnd = monthEnd.clone().add(6 - monthEnd.day(), "days");
  const weeks = [];

  for (let cursor = gridStart.clone(); cursor.isSameOrBefore(gridEnd, "day"); cursor.add(1, "week")) {
    weeks.push(
      Array.from({ length: 7 }).map((_, index) => cursor.clone().add(index, "days"))
    );
  }

  const today = moment().startOf("day");

  return (
    <div className="tw:mt-[16px] tw:overflow-hidden tw:rounded-[5px] tw:border tw:border-[#E6E6E6] tw:bg-white">
      <div className="tw:grid tw:grid-cols-7 tw:border-b tw:border-[#E6E6E6] tw:bg-[#FAFAFA]">
        {WEEKDAYS.map((day, index) => (
          <div
            key={day}
            className={cn(
              "tw:py-[10px] tw:text-center tw:text-[12px] tw:font-semibold tw:uppercase tw:tracking-[0.5px]",
              index === 0 ? "tw:!text-[#EA4335]" : index === 6 ? "tw:!text-[#20639B]" : "tw:!text-[#7C7B7B]",
            )}
          >
            {day}
          </div>
        ))}
      </div>

      {weeks.map((week) => (
        <div key={week[0].format("YYYY-MM-DD")} className="tw:grid tw:grid-cols-7">
          {week.map((day) => {
            const key = day.format("YYYY-MM-DD");
            const dayBookings = bookingsByDay.get(key) || [];
            const inMonth = day.isSame(month, "month");
            const isToday = day.isSame(today, "day");
            const isSelected = selectedDay === key;
            const hidden = dayBookings.length - VISIBLE_PER_DAY;

            return (
              <div
                key={key}
                role="button"
                tabIndex={0}
                onClick={() => onSelectDay(key)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectDay(key);
                  }
                }}
                className={cn(
                  "tw:min-h-[118px] tw:cursor-pointer tw:border-b tw:border-r tw:border-[#F0F0F0] tw:p-[6px] tw:transition-colors",
                  inMonth ? "tw:bg-white" : "tw:bg-[#FCFCFC]",
                  isSelected ? "tw:!bg-[#F2F7FB] tw:ring-1 tw:ring-inset tw:ring-[#20639B]" : "tw:hover:bg-[#FAFAFA]",
                )}
              >
                <div className="tw:mb-[6px] tw:flex tw:items-center tw:justify-between">
                  <span
                    className={cn(
                      "tw:flex tw:h-[22px] tw:min-w-[22px] tw:items-center tw:justify-center tw:rounded-full tw:px-1 tw:text-[12px]",
                      isToday ? "tw:bg-[#282828] tw:!text-white tw:font-semibold" : "",
                      !isToday && inMonth ? "tw:!text-[#282828]" : "",
                      !inMonth ? "tw:!text-[#C4C4C4]" : "",
                    )}
                  >
                    {day.date()}
                  </span>
                  {dayBookings.length ? (
                    <span className="tw:text-[11px] tw:!text-[#8D8D8D]">{dayBookings.length}</span>
                  ) : null}
                </div>

                <div className="tw:flex tw:flex-col tw:gap-[3px]">
                  {dayBookings.slice(0, VISIBLE_PER_DAY).map((booking) => (
                    <DayChip key={booking._id} booking={booking} />
                  ))}
                  {hidden > 0 ? (
                    <span className="tw:px-[6px] tw:text-[11px] tw:!text-[#7C7B7B]">+{hidden} more</span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default CalendarMonthGrid;
